import { useCallback } from 'react';

import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { EventSummary } from '@/lib/types';
import { getNextRelationshipGraphPosition } from '@/lib/relationship-graph-layout';

import { useFirebase } from '@/firebase';
import { useEntityAccessLayer } from '../../shared/hooks/use-entity-access-layer';
import { mapEventDTOToDomain } from '../api/mappers';
import type { EventFormValues } from '../schemas';
import type { Event } from '../types';
import {
  getEventInvolvementRelationshipChanges,
  getEventInvolvementRelationshipIds,
  hasEventInvolvementMembershipFields,
} from '../utils/event-involvement-relationships';

/**
 * Fields on the Event that are mirrored into the EventSummary.
 * When any of these change, the summary is automatically updated.
 */
const EVENT_SUMMARY_FIELDS = [
  'name',
  'type',
  'shortDescription',
  'mainImageUrl',
  'mainImagePath',
  'graphPosition',
  'order',
  'date',
  'characterIds',
  'organizationIds',
  'locationIds',
] as const;

/**
 * Centralised access layer for events.
 *
 * @example
 * ```tsx
 * const {
 *   summaries: events,
 *   event,
 *   sections,
 *   updateItem: updateEvent,
 *   deleteItem: deleteEvent,
 *   isLoading,
 * } = useEventAccessLayer(eventId);
 * ```
 */
export function useEventAccessLayer(eventId?: string) {
  const { backend } = useFirebase();
  const {
    summaries,
    item,
    sections,
    isLoading,
    error,
    updateItem: updateItemBase,
    deleteItem: deleteItemBase,
    getNewId,
  } = useEntityAccessLayer<Event, EventSummary>({
    entityType: 'events',
    summaryFieldKeys: EVENT_SUMMARY_FIELDS,
    itemId: eventId,
    itemMapper: mapEventDTOToDomain,
  });

  const {
    novel,
    deleteItem,
    saveItem,
    updateEntitySummary,
    batchSaveItemsNonBlocking,
    updateAllEventSummaries,
  } = useNovelRepository();

  const syncEventInvolvementRelationships = useCallback(
    async (
      id: string,
      previousData: Partial<EventFormValues> | null | undefined,
      nextData: Partial<EventFormValues> | null | undefined,
    ) => {
      const { relationshipsToCreate, relationshipIdsToDelete } = getEventInvolvementRelationshipChanges(
        id,
        previousData,
        nextData,
      );

      await Promise.all([
        ...relationshipsToCreate.map((relationship) => (
          saveItem('entities', 'relationships', relationship)
        )),
        ...relationshipIdsToDelete.map((relationshipId) => (
          deleteItem('entities', 'relationships', relationshipId)
        )),
      ]);
    },
    [deleteItem, saveItem],
  );

  /**
   * Creates a new event with initial data + summary.
   */
  const createEvent = useCallback(
    async (id: string, data: EventFormValues) => {
      const now = new Date().toISOString();
      const graphPosition = getNextRelationshipGraphPosition(novel);
      const newEvent = {
        ...data,
        id,
        audit: { createdAt: now, updatedAt: now },
        graphPosition,
      };

      // Persist item
      await saveItem('entities', 'events', newEvent);

      // Write summary
      const mainImageUrl = String(data.mainImageUrl ?? data.coverImageUrl ?? '');
      const mainImagePath = String((data as Record<string, unknown>).mainImagePath ?? '');
      await updateEntitySummary('events', id, {
        id,
        name: String(data.name ?? ''),
        type: String(data.type ?? ''),
        shortDescription: String(data.shortDescription ?? ''),
        mainImageUrl,
        mainImagePath,
        order: typeof data.order === 'number' ? data.order : 0,
        date: String(data.date ?? ''),
        characterIds: Array.isArray(data.characterIds) ? data.characterIds : [],
        organizationIds: Array.isArray(data.organizationIds) ? data.organizationIds : [],
        locationIds: Array.isArray(data.locationIds) ? data.locationIds : [],
        graphPosition,
        updatedAt: now,
      } as EventSummary);

      await syncEventInvolvementRelationships(id, null, data);
    },
    [novel, saveItem, syncEventInvolvementRelationships, updateEntitySummary],
  );

  const updateEvent = useCallback(
    async (id: string, partial: Record<string, unknown>) => {
      const previousData = item || summaries.find((summary) => summary.id === id);
      
      const previousCharacterIds = (previousData?.characterIds || []) as string[];
      const previousOrganizationIds = (previousData?.organizationIds || []) as string[];
      const previousLocationIds = (previousData?.locationIds || []) as string[];

      await updateItemBase(id, partial);

      if (backend && novel) {
        const newCharacterIds = (partial.characterIds !== undefined ? partial.characterIds : previousCharacterIds) as string[];
        const newOrganizationIds = (partial.organizationIds !== undefined ? partial.organizationIds : previousOrganizationIds) as string[];
        const newLocationIds = (partial.locationIds !== undefined ? partial.locationIds : previousLocationIds) as string[];

        const removedCharacters = previousCharacterIds.filter(eid => !newCharacterIds.includes(eid));
        const removedOrganizations = previousOrganizationIds.filter(eid => !newOrganizationIds.includes(eid));
        const removedLocations = previousLocationIds.filter(eid => !newLocationIds.includes(eid));

        if (removedCharacters.length > 0 || removedOrganizations.length > 0 || removedLocations.length > 0) {
          await Promise.all([
            ...removedCharacters.map(charId => backend.entities.deleteEventImplication(novel.ownerId, novel, id, 'characters', charId)),
            ...removedOrganizations.map(orgId => backend.entities.deleteEventImplication(novel.ownerId, novel, id, 'organizations', orgId)),
            ...removedLocations.map(locId => backend.entities.deleteEventImplication(novel.ownerId, novel, id, 'locations', locId))
          ]).catch(console.error);
        }
      }

      if (!hasEventInvolvementMembershipFields(partial)) return;

      await syncEventInvolvementRelationships(
        id,
        previousData as Partial<EventFormValues> | null | undefined,
        {
          ...(previousData || {}),
          ...partial,
        } as Partial<EventFormValues>,
      );
    },
    [item, summaries, syncEventInvolvementRelationships, updateItemBase],
  );

  const deleteEvent = useCallback(
    async (id: string) => {
      const previousData = item || summaries.find((summary) => summary.id === id);
      const deleteOrder = previousData?.order ?? -1;
      
      await deleteItemBase(id);

      const relationshipIds = getEventInvolvementRelationshipIds(
        previousData as Partial<EventFormValues> | null | undefined,
        id,
      );
      await Promise.all(
        relationshipIds.map((relationshipId) => deleteItem('entities', 'relationships', relationshipId)),
      );

      // Shift subsequent events down to fill the gap
      if (deleteOrder >= 0 && summaries.length > 0) {
        const eventsToShift = summaries.filter(e => e.id !== id && (e.order ?? 0) > deleteOrder);
        
        if (eventsToShift.length > 0) {
          const updatedEvents = eventsToShift.map(e => ({
            id: e.id,
            order: (e.order ?? 0) - 1,
          }));
          
          batchSaveItemsNonBlocking('entities', 'events', updatedEvents);
          
          const shiftedSummaries: Record<string, EventSummary> = {};
          eventsToShift.forEach(e => {
            shiftedSummaries[e.id] = { ...e, order: (e.order ?? 0) - 1 };
          });
          updateAllEventSummaries(shiftedSummaries);
        }
      }
    },
    [deleteItem, deleteItemBase, item, summaries, batchSaveItemsNonBlocking, updateAllEventSummaries],
  );

  return {
    /** Event summary list, sorted by order or updatedAt. */
    events: summaries,
    /** Single event domain model (if ID provided). */
    event: item,
    /** Event UI settings (sections/fields). */
    sections,
    isLoading,
    error,

    /** Partial-update an event. Auto-syncs summary if needed. */
    updateEvent,

    /** Delete an event and its summary. */
    deleteEvent,

    /** Create a new event. */
    createEvent,

    /** Generate a Firestore-compatible ID for a new event. */
    getNewId,
  };
}
